import { useState, useEffect, useRef, useCallback } from "react";
import SimplePeer from "simple-peer";
import { getToken } from "@/lib/api";
import { brutalfilesRecordTransfer } from "@/lib/api";

const WS_PATH = "/api/ws/brutalfiles";
const CHUNK_SIZE = 16 * 1024; // 16 KB
const CONNECT_TIMEOUT_MS = 25000;

// Module-level store: survives React component remounts (e.g. navigating away and back).
// shareId -> { files, onStatus }
const _persistedHostedShares = new Map();

function getWsUrl() {
  const token = getToken();
  if (!token) return null;
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host;
  return `${protocol}//${host}${WS_PATH}?token=${encodeURIComponent(token)}`;
}

/**
 * Hook for BrutalFiles P2P: WebSocket signaling + SimplePeer.
 * Returns: { peers, wsConnected, sendFileTo, receivingFile }
 *
 * NOTES:
 * - SimplePeer delivers ALL data (including JSON strings) as Uint8Array in the browser.
 *   Data handlers must try JSON.parse first; binary chunks naturally fail that parse.
 * - The receiver sends a "file_ack" JSON message after completing the download so the
 *   sender can resolve the promise and both peers close gracefully.
 */
export function useBrutalFilesP2P(myEmail) {
  const [peers, setPeers] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [receivingFile, setReceivingFile] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const shouldReconnectRef = useRef(true);
  const peerRef = useRef(null);
  const peerTargetRef = useRef(null);
  const receivingChunksRef = useRef([]);
  const receivingMetaRef = useRef(null);
  const publicHostedSharesRef = useRef(new Map()); // shareId -> { files, onStatus }
  const publicHostPeersRef = useRef(new Map()); // receiverId -> peer

  const reannounceHostedShares = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    Array.from(_persistedHostedShares.keys()).forEach((sid) => {
      try {
        wsRef.current.send(JSON.stringify({ type: "share_host", share_id: sid }));
      } catch (_) {}
    });
  }, []);

  const sendSignal = useCallback((toEmail, data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "signal", to: toEmail, data }));
    }
  }, []);

  useEffect(() => {
    const url = getWsUrl();
    if (!url || !myEmail) return;
    shouldReconnectRef.current = true;

    const connect = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        ws.send(JSON.stringify({ type: "introduce" }));
        // Re-host shares after reconnect so public page sees owner online again.
        reannounceHostedShares();
      };

      ws.onclose = () => {
        setWsConnected(false);
        setPeers([]);
        if (shouldReconnectRef.current) {
          reconnectTimerRef.current = setTimeout(connect, 1500);
        }
      };

      ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const type = msg.type || msg.event;

        if (type === "peers") {
          setPeers(Array.isArray(msg.peers) ? msg.peers : []);
          return;
        }
        if (type === "peer_joined" && msg.email) {
          setPeers((prev) => (prev.includes(msg.email) ? prev : [...prev, msg.email]));
          return;
        }
        if (type === "peer_left" && msg.email) {
          setPeers((prev) => prev.filter((e) => e !== msg.email));
          return;
        }

        if (type === "signal") {
          const fromEmail = (msg.from || "").trim().toLowerCase();
          if (!fromEmail) return;

          // Feed signal to existing peer for this contact
          if (peerRef.current && peerTargetRef.current === fromEmail) {
            peerRef.current.signal(msg.data);
            return;
          }

          // New incoming connection: tear down any previous peer and create receiver
          if (peerRef.current) {
            try { peerRef.current.destroy(); } catch (_) {}
          }
          const peer = new SimplePeer({ initiator: false, trickle: true });
          peerTargetRef.current = fromEmail;
          peerRef.current = peer;

          peer.on("signal", (data) => {
            sendSignal(fromEmail, data);
          });

          peer.on("connect", () => {
            // DataChannel open — ready to receive
          });

          peer.on("data", (rawData) => {
            // SimplePeer delivers ALL data as Uint8Array in the browser, even JSON strings.
            // Always try JSON.parse first; binary file chunks naturally fail JSON.parse.
            try {
              const str = typeof rawData === "string" ? rawData : new TextDecoder().decode(rawData);
              const obj = JSON.parse(str);
              if (obj && typeof obj.type === "string") {
                if (obj.type === "file_start") {
                  receivingMetaRef.current = {
                    fromEmail: peerTargetRef.current,
                    fileName: obj.name || "file",
                    size: obj.size || 0,
                  };
                  receivingChunksRef.current = [];
                  setReceivingFile({
                    fromEmail: peerTargetRef.current,
                    fileName: obj.name || "file",
                    size: obj.size || 0,
                    received: 0,
                  });
                  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                    new Notification("BrutalFiles", {
                      body: `${peerTargetRef.current} te envía ${obj.name || "un archivo"}`,
                    });
                  }
                } else if (obj.type === "file_end") {
                  const meta = receivingMetaRef.current;
                  if (meta) {
                    const blob = new Blob(receivingChunksRef.current);
                    const a = document.createElement("a");
                    a.href = URL.createObjectURL(blob);
                    a.download = meta.fileName || "download";
                    a.click();
                    URL.revokeObjectURL(a.href);
                    brutalfilesRecordTransfer(meta.fromEmail, myEmail, meta.fileName, meta.size).catch(() => {});
                  }
                  // Send ACK so the sender can resolve cleanly, then close our side
                  try { peer.send(JSON.stringify({ type: "file_ack" })); } catch (_) {}
                  receivingMetaRef.current = null;
                  receivingChunksRef.current = [];
                  setReceivingFile(null);
                  // Destroy our peer proactively so the remote close doesn't land as an error
                  setTimeout(() => {
                    try { peer.destroy(); } catch (_) {}
                    peerRef.current = null;
                    peerTargetRef.current = null;
                  }, 500);
                }
                return;
              }
            } catch (_) {
              // Not valid JSON → binary file chunk
            }

            // Binary chunk
            const meta = receivingMetaRef.current;
            if (meta) {
              const len = rawData?.byteLength ?? rawData?.length ?? 0;
              receivingChunksRef.current.push(rawData);
              setReceivingFile((prev) =>
                prev ? { ...prev, received: prev.received + len } : null
              );
            }
          });

          peer.on("error", () => {
            // Silently ignore errors after transfer is done (remote close triggers this)
            if (!receivingMetaRef.current) return;
            receivingMetaRef.current = null;
            receivingChunksRef.current = [];
            peerRef.current = null;
            peerTargetRef.current = null;
            setReceivingFile(null);
          });

          peer.on("close", () => {
            peerRef.current = null;
            peerTargetRef.current = null;
            setReceivingFile(null);
          });

          peer.signal(msg.data);
          return;
        }

        if (type === "share_host_ready" || type === "share_host_error") {
          const shareId = msg.share_id;
          if (!shareId) return;
          const hosted = _persistedHostedShares.get(shareId);
          if (hosted?.onStatus) {
            if (type === "share_host_ready") hosted.onStatus({ type: "host_ready", shareId });
            else hosted.onStatus({ type: "host_error", shareId, detail: msg.detail || "No se pudo activar el enlace" });
          }
          return;
        }

        if (type === "public_receiver_joined") {
          const shareId = msg.share_id;
          const receiverId = msg.receiver_id;
          const hosted = _persistedHostedShares.get(shareId);
          if (!hosted || !receiverId) return;
          if (hosted?.onStatus) hosted.onStatus({ type: "receiver_joined", shareId, receiverId });
          return;
        }

        if (type === "public_receiver_left") {
          const receiverId = msg.receiver_id;
          const peer = publicHostPeersRef.current.get(receiverId);
          if (peer) {
            try { peer.destroy(); } catch (_) {}
            publicHostPeersRef.current.delete(receiverId);
          }
          return;
        }

        if (type === "public_signal") {
          const shareId = msg.share_id;
          const receiverId = msg.from_receiver_id;
          if (!shareId || !receiverId) return;
          const hosted = _persistedHostedShares.get(shareId);
          if (!hosted) return;

          const ensurePeer = () => {
            let p = publicHostPeersRef.current.get(receiverId);
            if (p) return p;
            p = new SimplePeer({ initiator: false, trickle: true });
            publicHostPeersRef.current.set(receiverId, p);

            p.on("signal", (data) => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(
                  JSON.stringify({
                    type: "public_signal",
                    share_id: shareId,
                    to_receiver_id: receiverId,
                    data,
                  })
                );
              }
            });

            p.on("connect", async () => {
              try {
                const files = Array.isArray(hosted.files) ? hosted.files : [];
                p.send(JSON.stringify({ type: "share_batch_start", share_id: shareId, files_count: files.length }));
                for (let i = 0; i < files.length; i += 1) {
                  const file = files[i];
                  p.send(
                    JSON.stringify({
                      type: "file_start",
                      share_id: shareId,
                      name: file.name || "archivo",
                      size: file.size || 0,
                      mime: file.type || "",
                      index: i,
                      total: files.length,
                    })
                  );
                  let offset = 0;
                  while (offset < file.size) {
                    const chunk = file.slice(offset, offset + CHUNK_SIZE);
                    // eslint-disable-next-line no-await-in-loop
                    const buf = await chunk.arrayBuffer();
                    p.send(buf);
                    offset += buf.byteLength;
                    if (hosted?.onStatus) {
                      hosted.onStatus({
                        type: "sending_progress",
                        shareId,
                        receiverId,
                        fileIndex: i,
                        fileName: file.name,
                        sent: offset,
                        total: file.size,
                      });
                    }
                  }
                  p.send(JSON.stringify({ type: "file_end", share_id: shareId, index: i }));
                }
                p.send(JSON.stringify({ type: "share_batch_end", share_id: shareId }));
                if (hosted?.onStatus) hosted.onStatus({ type: "sending_complete", shareId, receiverId });
              } catch (err) {
                if (hosted?.onStatus) hosted.onStatus({ type: "sending_error", shareId, receiverId, detail: err?.message || "Error enviando archivos" });
              }
            });

            p.on("data", (rawData) => {
              try {
                const str = typeof rawData === "string" ? rawData : new TextDecoder().decode(rawData);
                const obj = JSON.parse(str);
                if (obj.type === "share_ack") {
                  if (hosted?.onStatus) hosted.onStatus({ type: "receiver_done", shareId, receiverId });
                  try { p.destroy(); } catch (_) {}
                  publicHostPeersRef.current.delete(receiverId);
                }
              } catch (_) {}
            });

            p.on("error", () => {
              if (hosted?.onStatus) hosted.onStatus({ type: "peer_error", shareId, receiverId });
            });

            p.on("close", () => {
              publicHostPeersRef.current.delete(receiverId);
            });

            return p;
          };

          const peer = ensurePeer();
          peer.signal(msg.data);
          return;
        }
      } catch (e) {
        console.error("BrutalFiles WS parse error:", e);
      }
      };

      ws.onerror = () => {
        setWsConnected(false);
      };
    };
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
      }
      wsRef.current = null;
      if (peerRef.current) {
        try { peerRef.current.destroy(); } catch (_) {}
        peerRef.current = null;
      }
      Array.from(publicHostPeersRef.current.values()).forEach((peer) => {
        try { peer.destroy(); } catch (_) {}
      });
      publicHostPeersRef.current.clear();
      // DO NOT clear _persistedHostedShares here: it must survive remounts so that
      // reannounceHostedShares() can re-host after the user navigates away and back.
      publicHostedSharesRef.current.clear();
      peerTargetRef.current = null;
      setPeers([]);
      setWsConnected(false);
      setReceivingFile(null);
    };
  }, [myEmail, sendSignal, reannounceHostedShares]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") reannounceHostedShares();
    };
    const onFocus = () => {
      reannounceHostedShares();
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisible);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("focus", onFocus);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisible);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("focus", onFocus);
      }
    };
  }, [reannounceHostedShares]);

  useEffect(() => {
    if (!wsConnected || _persistedHostedShares.size === 0) return undefined;
    const interval = setInterval(() => {
      reannounceHostedShares();
    }, 5000);
    return () => clearInterval(interval);
  }, [wsConnected, reannounceHostedShares]);

  const sendFileTo = useCallback(
    (toEmail, file, onProgress) => {
      const to = (toEmail || "").trim().toLowerCase();
      if (!to || !file || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error("No conectado o datos inválidos"));
      }

      if (peerRef.current) {
        try { peerRef.current.destroy(); } catch (_) {}
        peerRef.current = null;
      }

      return new Promise((resolve, reject) => {
        const peer = new SimplePeer({ initiator: true, trickle: true });
        peerTargetRef.current = to;
        peerRef.current = peer;

        let resolved = false;
        const finish = (err) => {
          if (resolved) return;
          resolved = true;
          try {
            if (connectTimeout) clearTimeout(connectTimeout);
            peer.destroy();
          } catch (_) {}
          peerRef.current = null;
          peerTargetRef.current = null;
          if (err) reject(err);
          else resolve();
        };

        const connectTimeout = setTimeout(() => {
          finish(new Error("Tiempo de espera agotado. Asegúrate de que la otra persona tiene la pestaña Archivos abierta."));
        }, CONNECT_TIMEOUT_MS);

        peer.on("signal", (data) => {
          sendSignal(to, data);
        });

        peer.on("connect", () => {
          clearTimeout(connectTimeout);
          if (onProgress) onProgress(0, file.size);
          peer.send(JSON.stringify({ type: "file_start", name: file.name, size: file.size }));
          const chunkSize = CHUNK_SIZE;
          let offset = 0;
          const reader = new FileReader();

          // Listen for file_ack from receiver to resolve cleanly
          peer.on("data", (data) => {
            try {
              const str = typeof data === "string" ? data : new TextDecoder().decode(data);
              const obj = JSON.parse(str);
              if (obj.type === "file_ack") finish();
            } catch (_) {}
          });

          reader.onload = function readNext() {
            const buf = reader.result;
            if (buf && buf.byteLength > 0) {
              peer.send(buf);
              offset += buf.byteLength;
              if (onProgress) onProgress(offset, file.size);
            }
            if (offset < file.size) {
              const slice = file.slice(offset, offset + chunkSize);
              reader.readAsArrayBuffer(slice);
            } else {
              peer.send(JSON.stringify({ type: "file_end" }));
              if (onProgress) onProgress(file.size, file.size);
            }
          };

          reader.onerror = () => {
            finish(new Error("Error leyendo archivo"));
          };

          const slice = file.slice(0, chunkSize);
          reader.readAsArrayBuffer(slice);
        });

        peer.on("error", (err) => {
          finish(err || new Error("Error de conexión P2P"));
        });

        peer.on("close", () => {
          if (!resolved) {
            finish(new Error("Conexión cerrada antes de completar el envío"));
          }
          peerRef.current = null;
          peerTargetRef.current = null;
        });
      });
    },
    [sendSignal]
  );

  return {
    peers,
    wsConnected,
    sendFileTo,
    receivingFile,
    requestNotificationPermission: () => {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        Notification.requestPermission();
      }
    },
    hostPublicShare: (shareId, files, onStatus) => {
      const sid = (shareId || "").trim();
      if (!sid) throw new Error("shareId inválido");
      if (!Array.isArray(files) || files.length === 0) throw new Error("No hay archivos para compartir");
      // Store in module-level map so it survives component remounts (navigation away and back).
      _persistedHostedShares.set(sid, { files, onStatus });
      // Also store in the hook ref for internal handlers (same object reference).
      publicHostedSharesRef.current.set(sid, { files, onStatus });
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "share_host", share_id: sid }));
      }
      // If WS is not ready yet, reannounceHostedShares() in ws.onopen will send it.
      return () => {
        _persistedHostedShares.delete(sid);
        publicHostedSharesRef.current.delete(sid);
      };
    },
  };
}
